import {
  Alert,
  AlertType,
  AlertsCommentary,
  getOrderedAlertPageGroups,
  nonDismissibleAlertTypes,
} from '@aloft/toolkit-shared';
import { Button, Flex, Icon, Input, Typography, useThemeColor } from '@aloft/toolkit-sui';

import { debounce, isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { schPropertyGridCellKey } from '../../../components/PropertyGrid/types.ts';
import { useZodForm } from '../../../hooks/useZodForm.ts';
import { routeToStepMap } from '../../../hooks/validation/consts.ts';
import { cancelDismissAlert, dismissAlert } from '../../../redux/features/alertsSlice.ts';
import { setCommentaryV2 } from '../../../redux/features/commentarySlice.ts';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectIsInspectifyOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { setFocusedPropertyGridCellKey } from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { getAlertPageLabel } from './getAlertPageLabel.ts';
import { useAlerts } from './hooks/useAlerts.tsx';

const AlertItem = ({ alert }: { alert: Alert }) => {
  const {
    type,
    status,
    severity,
    message,
    fixingInfo: { tips, pairedCostRouteItem, fixingMethod, targetCellKey, commentAlwaysRequired },
  } = alert;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);

  const { control } = useFormContext<AlertsCommentary>();

  const isDismissed = status === 'dismissed';
  const isNonDismissable = nonDismissibleAlertTypes.includes(type);
  const canDismiss = !isNonDismissable;
  const hasAction = fixingMethod.includes('action');
  const hasComment = fixingMethod.includes('comment');
  const showFixNow = hasAction && isInspectifyOrder && pairedCostRouteItem && status === 'active';
  const showIgnore = !isDismissed && canDismiss && severity === 'warning' && hasComment;
  const showExplain = !isDismissed && canDismiss && severity === 'error' && hasComment;

  return (
    <Flex key={type} flexDirection="column" gap="8px" alignItems="flex-end" width="100%">
      <Flex gap="8px" alignItems="flex-start" width="100%">
        <Icon
          icon={severity === 'error' ? 'information-close' : 'error-warning'}
          color={severity === 'error' ? 'alertError' : 'statusWarning'}
          size="lg"
        />
        <Flex flex={1} minWidth={0}>
          <Typography variant="body1">{message}</Typography>
        </Flex>
      </Flex>

      <Flex flexDirection="column" width="100%" paddingLeft="32px">
        <Typography variant="body2" color="surfaceAccent4">
          {tips}
        </Typography>
      </Flex>

      {(isDismissed || commentAlwaysRequired) && (
        <Controller
          name={type}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Flex width="100%" flexDirection="column" gap="4px" paddingLeft="32px">
              <Input
                type="multiline"
                resize="vertical"
                __dangerDoNotUseSx={
                  error ? { borderColor: useThemeColor('alertError') } : undefined
                }
                value={value}
                onClear={() => onChange('')}
                onChange={onChange}
                placeholderText="Text entered here will appear in the final report. Please enter accurate, professional content."
              />
              <Flex justifyContent="space-between" alignItems="center" width="100%">
                {error ? (
                  <Typography variant="body2" color="alertError">
                    Required
                  </Typography>
                ) : (
                  <Flex />
                )}
                {canDismiss && !commentAlwaysRequired && (
                  <Button
                    variant="inline"
                    size="sm"
                    text="Cancel Dismiss"
                    onClick={() => dispatch(cancelDismissAlert(type))}
                  />
                )}
              </Flex>
            </Flex>
          )}
        />
      )}

      {!isDismissed && (showFixNow || showIgnore || showExplain) && (
        <Flex width="100%" gap="8px" justifyContent="flex-end">
          {showFixNow && (
            <Button
              size="sm"
              text="Fix Now"
              onClick={() => {
                if (targetCellKey) {
                  const parsed = schPropertyGridCellKey.safeParse(targetCellKey);
                  if (parsed.success) dispatch(setFocusedPropertyGridCellKey(parsed.data));
                }
                navigate(`paired-cost/${pairedCostRouteItem}`);
              }}
            />
          )}
          {showIgnore && (
            <Button
              variant="secondary"
              size="sm"
              text="Ignore"
              onClick={() => dispatch(dismissAlert(type))}
            />
          )}
          {showExplain && (
            <Button
              variant="secondary"
              size="sm"
              text="Explain"
              onClick={() => dispatch(dismissAlert(type))}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};

export const AlertsContent = ({ alerts }: { alerts: ReturnType<typeof useAlerts> }) => {
  const dispatch = useAppDispatch();

  const commentaryV2 = useAppSelector(selectCommentaryV2);

  const pageGroups = useMemo(() => getOrderedAlertPageGroups(alerts, routeToStepMap), [alerts]);

  const dismissedAlertTypes = useMemo(
    () =>
      pageGroups
        .flatMap(({ alerts: pageAlerts }) => pageAlerts)
        .filter(({ status }) => status === 'dismissed' || status === 'resolved_pending_comment')
        .map(({ type }) => type),
    [pageGroups],
  );

  const schDismissedAlertsCommentary = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          dismissedAlertTypes.map((type) => [type, z.string().min(5, 'This field is required')]),
        ),
      ),
    [dismissedAlertTypes],
  );

  const zodForm = useZodForm({
    schema: schDismissedAlertsCommentary,
    defaultValues: useMemo(() => commentaryV2.alerts ?? {}, []),
    mode: 'all',
  });

  const { watch } = zodForm;

  const debouncedSetCommentary = useMemo(
    () =>
      debounce((newAlertsCommentary: AlertsCommentary | undefined) => {
        if (isEqual(newAlertsCommentary, commentaryV2.alerts)) return;

        dispatch(
          setCommentaryV2({
            ...commentaryV2,
            alerts: newAlertsCommentary,
          }),
        );
      }, 300),
    [dispatch, commentaryV2],
  );

  useEffect(() => {
    zodForm.trigger();
  }, [dismissedAlertTypes, zodForm]);

  useEffect(() => {
    const subscription = watch((formValues) => {
      const cleanedFormData = Object.entries(formValues)
        .map(([k, v]) => [k, v === '' ? undefined : v?.trim()])
        .filter(([_k, v]) => v !== undefined) as [AlertType, string][];

      debouncedSetCommentary(
        cleanedFormData.length ? Object.fromEntries(cleanedFormData) : undefined,
      );
    });

    return () => subscription.unsubscribe();
  }, [dispatch, watch, debouncedSetCommentary]);

  if (pageGroups.length === 0) {
    return <Typography>🎉 Congrats, no alerts remain!</Typography>;
  }

  return (
    <FormProvider {...zodForm}>
      {pageGroups.map(({ page, alerts: pageAlerts }) => (
        <Flex key={page} flexDirection="column" gap="16px" width="100%">
          <Typography variant="h3">{getAlertPageLabel(page)}</Typography>
          <Flex flexDirection="column" gap="16px" width="100%">
            {pageAlerts.map((alert) => (
              <AlertItem key={alert.type} alert={alert} />
            ))}
          </Flex>
        </Flex>
      ))}
    </FormProvider>
  );
};
